import { cn } from '@shadcn/lib/utils';
import { Calendar } from '@shadcn/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@shadcn/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import React, { useId, useState } from 'react';

import { Button } from '../button/button';

type PatientDatePickerProps = {
  readonly date: Date | undefined;
  readonly setDate: (date: Date | undefined) => void;
  readonly id?: string;
  readonly ariaLabelledby?: string;
  readonly className?: string;
  readonly buttonText?: string;
  readonly captionLayout?: React.ComponentProps<typeof Calendar>['captionLayout'];
  readonly fromYear?: number;
  readonly toYear?: number;
};

function PatientDatePicker({
  date,
  setDate,
  id: incomingId,
  ariaLabelledby,
  className,
  buttonText,
  captionLayout,
  fromYear,
  toYear,
}: PatientDatePickerProps) {
  const generatedId = useId();
  const [open, setOpen] = useState(false);

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <div className={cn('flex w-full', className)}>
          <Button
            appearance="secondary"
            aria-labelledby={ariaLabelledby}
            className="w-full justify-between h-[45px] rounded-md border-neutral-400 shadow-sm text-lg text-black px-[12px]"
            id={incomingId ?? generatedId}
            size="sm"
          >
            <span>{buttonText || (date ? format(date, 'PPP') : 'Select a date')}</span>
            <CalendarIcon aria-hidden="true" className="h-4 w-4 text-neutral-700" />
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          captionLayout={captionLayout}
          defaultMonth={date}
          disabled={(calendarDate) => calendarDate > new Date()}
          fromYear={fromYear}
          initialFocus
          mode="single"
          onSelect={(d) => {
            setDate(d);
            if (d) {
              setOpen(false);
            }
          }}
          selected={date}
          toYear={toYear}
        />
      </PopoverContent>
    </Popover>
  );
}

export { PatientDatePicker };
export type { PatientDatePickerProps };
